import { POSTGRES_INT4_MAX } from '@designdeck/shared';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Button,
  Card,
  EmptyState,
  FormField,
  Input,
  LoadingState,
  PageHeader,
  Select,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui';
import type { StatusPillProps } from '@/components/ui';
import {
  ADMIN_DESIGN_STATUSES,
  type AdminDesignMockupCounts,
  type AdminDesignQuery,
  type AdminDesignStatus,
} from '@/api/admin';
import { useAdminDesigns } from '@/hooks/useAdminDesigns';
import {
  DESIGN_STATUS_LABEL,
  DESIGN_STATUS_VARIANT,
  formatAdminDate,
} from './admin-design-labels';
import { ADMIN_SEARCH_DEBOUNCE_MS, parsePage } from './admin-list-params';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** URL search params the page reads and writes; shareable as a link. */
const ADMIN_DESIGN_PARAMS = {
  ownerUserId: 'ownerUserId',
  externalDesignerId: 'externalDesignerId',
  status: 'status',
  createdFrom: 'createdFrom',
  createdTo: 'createdTo',
  page: 'page',
} as const;

type FilterParam = Exclude<
  (typeof ADMIN_DESIGN_PARAMS)[keyof typeof ADMIN_DESIGN_PARAMS],
  'page'
>;

function parseDesignerId(value: string | null): number | undefined {
  if (!value || !/^\d+$/.test(value)) return undefined;
  const id = Number(value);
  return id >= 1 && id <= POSTGRES_INT4_MAX ? id : undefined;
}

function parseStatus(value: string | null): AdminDesignStatus | undefined {
  return ADMIN_DESIGN_STATUSES.find((status) => status === value);
}

function parseDate(value: string | null): string | undefined {
  return value && DATE_ONLY.test(value) ? value : undefined;
}

type MockupState = 'none' | 'complete' | 'failed' | 'in-progress';

/**
 * Mockup state of a row: failures win over everything, "complete" needs at
 * least one expected mockup, a design without mockups has no state at all.
 */
function mockupState({
  completed,
  failed,
  expected,
}: AdminDesignMockupCounts): MockupState {
  if (failed > 0) return 'failed';
  if (expected === 0) return 'none';
  if (completed === expected) return 'complete';
  return 'in-progress';
}

function MockupCell({ counts }: { counts: AdminDesignMockupCounts }) {
  const state = mockupState(counts);
  if (state === 'none') {
    return (
      <span className="text-mute" data-mockup-state={state}>
        —
      </span>
    );
  }

  const variant: StatusPillProps['variant'] =
    state === 'failed'
      ? 'danger'
      : state === 'complete'
        ? 'published'
        : 'warning';
  const note =
    state === 'failed'
      ? `${counts.failed} failed`
      : state === 'complete'
        ? 'Complete'
        : 'In progress';

  return (
    <div className="flex items-center gap-2" data-mockup-state={state}>
      <StatusPill variant={variant}>
        {counts.completed}/{counts.expected}
      </StatusPill>
      <span
        className={
          state === 'failed' ? 'text-xs text-danger' : 'text-xs text-mute'
        }
      >
        {note}
      </span>
    </div>
  );
}

/**
 * Admin: designs across all accounts, newest first. Filters and page live
 * in the URL so a filtered view is shareable and other admin pages can
 * link into it (e.g. `?ownerUserId=…`). Read-only; the detail page shows
 * products, colors and mockups of one design.
 */
export function AdminDesignsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlOwner = searchParams.get(ADMIN_DESIGN_PARAMS.ownerUserId) ?? '';
  const urlDesigner =
    searchParams.get(ADMIN_DESIGN_PARAMS.externalDesignerId) ?? '';
  const status = parseStatus(searchParams.get(ADMIN_DESIGN_PARAMS.status));
  const createdFrom = parseDate(
    searchParams.get(ADMIN_DESIGN_PARAMS.createdFrom)
  );
  const createdTo = parseDate(searchParams.get(ADMIN_DESIGN_PARAMS.createdTo));
  const page = parsePage(searchParams.get(ADMIN_DESIGN_PARAMS.page));
  const designerId = parseDesignerId(urlDesigner);

  const [ownerInput, setOwnerInput] = useState(urlOwner);
  const [designerInput, setDesignerInput] = useState(urlDesigner);

  // Follow URL changes from outside the inputs (nav link, back button)
  useEffect(() => setOwnerInput(urlOwner), [urlOwner]);
  useEffect(() => setDesignerInput(urlDesigner), [urlDesigner]);

  useEffect(() => {
    document.title = 'Design Admin – DesignDeck';
    return () => {
      document.title = 'DesignDeck';
    };
  }, []);

  /** Writes one filter to the URL and restarts at the first page. */
  const setFilter = (name: FilterParam, value: string, replace = false) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(name, value);
        else next.delete(name);
        next.delete(ADMIN_DESIGN_PARAMS.page);
        return next;
      },
      { replace }
    );
  };

  // Debounced text filters; only a changed value touches the URL, so the
  // page survives mounting with a prefilled field.
  useEffect(() => {
    const timer = setTimeout(() => {
      const owner = ownerInput.trim();
      if (owner !== urlOwner) {
        setFilter(ADMIN_DESIGN_PARAMS.ownerUserId, owner, true);
      }
    }, ADMIN_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only typing starts the debounce
  }, [ownerInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const designer = designerInput.trim();
      // An invalid id stays in the field with its error, not in the URL
      const valid = designer === '' || parseDesignerId(designer) !== undefined;
      if (valid && designer !== urlDesigner) {
        setFilter(ADMIN_DESIGN_PARAMS.externalDesignerId, designer, true);
      }
    }, ADMIN_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only typing starts the debounce
  }, [designerInput]);

  const setPage = (nextPage: number) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (nextPage > 1) next.set(ADMIN_DESIGN_PARAMS.page, String(nextPage));
      else next.delete(ADMIN_DESIGN_PARAMS.page);
      return next;
    });
  };

  const clearFilters = () => {
    setOwnerInput('');
    setDesignerInput('');
    setSearchParams(new URLSearchParams());
  };

  const query: AdminDesignQuery = {
    ownerUserId: urlOwner || undefined,
    externalDesignerId: designerId,
    status,
    createdFrom,
    createdTo,
    page,
  };

  const { data: result, isLoading, isError, refetch } = useAdminDesigns(query);

  const designs = result?.data ?? [];
  const meta = result?.meta;

  const activeFilters = [
    designerId !== undefined ? `designer ${designerId}` : null,
    urlOwner ? `owner ${urlOwner}` : null,
    status ? `status ${DESIGN_STATUS_LABEL[status]}` : null,
    createdFrom ? `created from ${createdFrom}` : null,
    createdTo ? `created to ${createdTo}` : null,
  ].filter((filter): filter is string => filter !== null);

  const designerInvalid =
    designerInput.trim() !== '' &&
    parseDesignerId(designerInput.trim()) === undefined;

  const isEmpty = !isLoading && !isError && designs.length === 0;
  const listSearch = searchParams.toString();

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <PageHeader
        eyebrow="Admin"
        title="Designs"
        description="Designs across all accounts, newest first, with their mockup state."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <FormField
          label="Designer ID"
          error={designerInvalid ? 'Enter a whole number.' : undefined}
        >
          <Input
            inputMode="numeric"
            value={designerInput}
            onChange={(e) => setDesignerInput(e.target.value)}
            placeholder="e.g. 29"
          />
        </FormField>
        <FormField label="Owner user ID">
          <Input
            value={ownerInput}
            onChange={(e) => setOwnerInput(e.target.value)}
            placeholder="User id (cuid)"
          />
        </FormField>
        <FormField label="Status">
          <Select
            value={status ?? ''}
            onChange={(e) =>
              setFilter(ADMIN_DESIGN_PARAMS.status, e.target.value)
            }
          >
            <option value="">All statuses</option>
            {ADMIN_DESIGN_STATUSES.map((value) => (
              <option key={value} value={value}>
                {DESIGN_STATUS_LABEL[value]}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Created from">
          <Input
            type="date"
            value={createdFrom ?? ''}
            onChange={(e) =>
              setFilter(ADMIN_DESIGN_PARAMS.createdFrom, e.target.value)
            }
          />
        </FormField>
        <FormField label="Created to">
          <Input
            type="date"
            value={createdTo ?? ''}
            onChange={(e) =>
              setFilter(ADMIN_DESIGN_PARAMS.createdTo, e.target.value)
            }
          />
        </FormField>
      </div>

      {isLoading && <LoadingState label="Loading designs…" />}

      {isError && (
        <Card className="flex flex-col items-center gap-4 text-center">
          <p className="text-sm text-danger">Failed to load designs.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Retry
          </Button>
        </Card>
      )}

      {isEmpty &&
        (activeFilters.length > 0 ? (
          <EmptyState
            title="No designs match these filters"
            description={`Active filters: ${activeFilters.join(', ')}.`}
            action={
              <Button variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <EmptyState
            title="No designs yet"
            description="Designs appear here once a seller or an import creates them."
          />
        ))}

      {!isError && designs.length > 0 && (
        <Table>
          <TableHead>
            <tr>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>SKU</TableHeaderCell>
              <TableHeaderCell>Owner</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Source</TableHeaderCell>
              <TableHeaderCell>Created</TableHeaderCell>
              <TableHeaderCell>Published</TableHeaderCell>
              <TableHeaderCell>Mockups</TableHeaderCell>
            </tr>
          </TableHead>
          <TableBody>
            {designs.map((design) => (
              <TableRow key={design.id}>
                <TableCell>
                  <Link
                    to={`/admin/designs/${design.id}`}
                    state={{ listSearch }}
                    className="font-semibold text-ink underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {design.name}
                  </Link>
                </TableCell>
                <TableCell className="font-mono text-xs text-ink-2">
                  {design.sku}
                </TableCell>
                <TableCell>
                  <Link
                    to={`/admin/users?search=${encodeURIComponent(design.owner.email)}`}
                    className="block truncate text-ink underline-offset-2 hover:underline"
                  >
                    {design.owner.email}
                  </Link>
                  <span className="font-mono text-xs text-mute">
                    {design.owner.shirteeDesignerId !== null
                      ? `Designer ${design.owner.shirteeDesignerId}`
                      : 'No designer id'}
                  </span>
                </TableCell>
                <TableCell>
                  <StatusPill variant={DESIGN_STATUS_VARIANT[design.status]}>
                    {DESIGN_STATUS_LABEL[design.status]}
                  </StatusPill>
                </TableCell>
                <TableCell className="font-mono text-xs text-ink-2">
                  {design.createdVia ?? '—'}
                </TableCell>
                <TableCell className="whitespace-nowrap text-ink-2">
                  {formatAdminDate(design.createdAt)}
                </TableCell>
                <TableCell className="whitespace-nowrap text-ink-2">
                  {formatAdminDate(design.publishedAt)}
                </TableCell>
                <TableCell>
                  <MockupCell counts={design.mockups} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {meta && meta.totalPages > 1 && (
        <nav
          aria-label="Pagination"
          className="flex flex-wrap items-center justify-center gap-4"
        >
          <Button
            variant="outline"
            onClick={() => setPage(Math.max(1, meta.page - 1))}
            disabled={!meta.hasPreviousPage}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Previous
          </Button>
          <span className="font-mono text-sm text-mute">
            Page {meta.page} of {meta.totalPages} · {meta.total} designs
          </span>
          <Button
            variant="outline"
            onClick={() => setPage(meta.page + 1)}
            disabled={!meta.hasNextPage}
          >
            Next
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </nav>
      )}
    </div>
  );
}
